/**
 * T264: Audit Logs Page
 * 
 * Admin-only page for viewing and filtering audit log entries.
 * Features:
 * - Display audit logs in table format
 * - Filter by event type, user, admin, date range, IP address
 * - Pagination support
 * - CSV export functionality
 * - Real-time statistics
 *
 * Actors are shown by name, not id. The API snapshots the username onto each row at write
 * time because `user_id`/`admin_id` are ON DELETE SET NULL — so for anyone since deleted the
 * id is gone and the name is the only attribution left. Filtering is still by id, which is
 * what the endpoint takes; the full id is on hover. See `Actor`.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import { fetcher } from '../../utils/fetcher';
import EmptyStateCard from '../../components/EmptyStateCard';

interface AuditLog {
  id: string;
  event_type: string;
  user_id?: string;
  admin_id?: string;
  /**
   * The actor's name as it was when the event happened, snapshotted by the API at write
   * time. `user_id`/`admin_id` are ON DELETE SET NULL, so for anyone since deleted the id
   * is gone and this is the only remaining record of who acted.
   */
  user_username?: string;
  admin_username?: string;
  timestamp: string;
  ip_address?: string;
  details?: Record<string, any>;
}

/**
 * An audit row's actor, preferring the snapshotted name over the id.
 *
 * Three states, and the third is the point of the snapshot:
 *   - name and id: the name, with the full id on hover for filtering.
 *   - id only: a row written before the snapshot columns existed. Truncated id, as before.
 *   - name only: the actor has been deleted. The name is all that survives, and showing it
 *     is what keeps the trail readable — an audit row with no actor is worthless for the
 *     purpose the log exists to serve.
 */
const Actor: React.FC<{ name?: string; id?: string }> = ({ name, id }) => {
  if (!name) {
    return (
      <span className="font-mono text-xs">{id ? id.slice(0, 8) + '…' : '-'}</span>
    );
  }

  return (
    <span title={id ?? 'This account has since been deleted'}>
      {name}
      {!id && (
        <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(deleted)</span>
      )}
    </span>
  );
};

interface AuditResponse {
  logs: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

const AuditLogsPage: React.FC = () => {
  const { isPlatformAdmin } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    event_type: '',
    user_id: '',
    admin_id: '',
    start_date: '',
    end_date: '',
    ip_address: '',
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  
  // Statistics state
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    loadAuditLogs();
    loadStatistics();
  }, [filters, currentPage, pageSize]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      
      // Add filters
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.admin_id) params.append('admin_id', filters.admin_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.ip_address) params.append('ip_address', filters.ip_address);
      
      // Add pagination
      params.append('limit', pageSize.toString());
      params.append('offset', ((currentPage - 1) * pageSize).toString());
      
      const url = `/api/v1/audit/read?${params.toString()}`;
      const data: AuditResponse = await fetcher(url);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load audit logs';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const url = `/api/v1/audit/stats?${params.toString()}`;
      const data = await fetcher(url);
      setStats(data);
    } catch (err) {
      console.error('Failed to load audit statistics:', err);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      event_type: '',
      user_id: '',
      admin_id: '',
      start_date: '',
      end_date: '',
      ip_address: '',
    });
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    if (logs.length === 0) {
      alert('No logs to export');
      return;
    }

    // Names as well as ids: this export is what gets handed to an auditor, and for anyone
    // since deleted the id column is empty — the snapshotted name is the only attribution
    // left. The ids stay because they are what the filters above take.
    const headers = [
      'ID', 'Event Type', 'User', 'User ID', 'Admin', 'Admin ID',
      'Timestamp', 'IP Address', 'Details',
    ];
    const rows = logs.map(log => [
      log.id,
      log.event_type,
      log.user_username || '',
      log.user_id || '',
      log.admin_username || '',
      log.admin_id || '',
      new Date(log.timestamp).toLocaleString(),
      log.ip_address || '',
      JSON.stringify(log.details || {}),
    ]);

    const csvContent = [
      headers.join(','),
      // A quote inside a quoted field is escaped by doubling it (RFC 4180). Without this the
      // Details column breaks every row it appears in, because it holds JSON — the export
      // has been producing malformed CSV for any event carrying details.
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_logs_${new Date().toISOString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(total / pageSize);
  const eventTypes = [
    'LOGIN_SUCCESS',
    'LOGIN_FAILURE',
    'REGISTRATION_SUCCESS',
    'REGISTRATION_FAILURE',
    'REGISTRATION_APPROVAL',
    'REGISTRATION_REJECTION',
    'PASSWORD_RESET_REQUEST',
    'PASSWORD_RESET_COMPLETE',
    'USER_DEACTIVATED',
    'USER_REACTIVATED',
    'ROLE_ASSIGNMENT',
    'ROLE_REMOVAL',
    'PERMISSION_DENIED',
    'SQL_INJECTION_ATTEMPT',
  ];

  // This log spans every workspace, so it is Akhile's back office — the same tier the route is
  // guarded by. Reading the deprecated `system_role` here refused the platform admins the route
  // had just let through.
  if (!isPlatformAdmin) {
    return (
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
        <div className="rounded-lg px-4 py-3 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
          <p className="font-bold">Access Denied</p>
          <p>This page is only available to platform administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Audit Logs
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Security monitoring and compliance reporting
        </p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card variant="default">
            <Card.Body>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Event Summary
              </h3>
              <div className="space-y-2">
                {stats.event_totals && Object.entries(stats.event_totals).map(([event, count]: [string, any]) => (
                  <div key={event} className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{event}</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{count}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>

          <Card variant="default">
            <Card.Body>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Filters Applied
              </h3>
              <div className="space-y-1 text-sm">
                {Object.entries(filters).map(([key, value]) => (
                  value && (
                    <div key={key} className="text-gray-600 dark:text-gray-400">
                      <strong>{key.replace('_', ' ')}:</strong> {value}
                    </div>
                  )
                ))}
              </div>
              {!Object.values(filters).some(v => v) && (
                <p className="text-gray-600 dark:text-gray-400">No filters applied</p>
              )}
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <Card.Body>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Filters
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <Select
              label="Event Type"
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              options={[
                { value: '', label: 'All Events' },
                ...eventTypes.map(type => ({ value: type, label: type })),
              ]}
            />
            
            <Input
              type="text"
              label="User ID"
              value={filters.user_id}
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
              placeholder="Filter by user ID"
            />
            
            <Input
              type="text"
              label="Admin ID"
              value={filters.admin_id}
              onChange={(e) => handleFilterChange('admin_id', e.target.value)}
              placeholder="Filter by admin ID"
            />
            
            <Input
              type="date"
              label="Start Date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
            
            <Input
              type="date"
              label="End Date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
            
            <Input
              type="text"
              label="IP Address"
              value={filters.ip_address}
              onChange={(e) => handleFilterChange('ip_address', e.target.value)}
              placeholder="Filter by IP address"
            />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
            <Button variant="primary" onClick={handleExportCSV} disabled={loading}>
              Export CSV
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-error/10 dark:bg-error/20 border border-error/30 text-error">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-container min-h-64">
          <div className="spinner" />
        </div>
      )}

      {/* Logs Table */}
      {!loading && logs.length > 0 && (
        <>
          <Card className="mb-6 overflow-x-auto">
            <Card.Body>
              <table className="w-full text-sm">
                <thead className="border-b-2 border-gray-200 dark:border-gray-700">
                  <tr className="text-left">
                    <th className="pb-3 font-semibold text-gray-900 dark:text-white">Timestamp</th>
                    <th className="pb-3 font-semibold text-gray-900 dark:text-white">Event Type</th>
                    <th className="pb-3 font-semibold text-gray-900 dark:text-white">User</th>
                    <th className="pb-3 font-semibold text-gray-900 dark:text-white">Admin</th>
                    <th className="pb-3 font-semibold text-gray-900 dark:text-white">IP Address</th>
                    <th className="pb-3 font-semibold text-gray-900 dark:text-white">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                    >
                      <td className="py-3 text-gray-700 dark:text-gray-300">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                          {log.event_type}
                        </span>
                      </td>
                      <td className="py-3 text-gray-700 dark:text-gray-300 text-xs">
                        <Actor name={log.user_username} id={log.user_id} />
                      </td>
                      <td className="py-3 text-gray-700 dark:text-gray-300 text-xs">
                        <Actor name={log.admin_username} id={log.admin_id} />
                      </td>
                      <td className="py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">
                        {log.ip_address || '-'}
                      </td>
                      <td className="py-3 text-gray-700 dark:text-gray-300 text-xs">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <details>
                            <summary className="cursor-pointer text-primary-600 dark:text-primary-400">
                              View
                            </summary>
                            <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card.Body>
          </Card>

          {/* Pagination */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} logs
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              
              <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm">
                Page {currentPage} of {totalPages}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && logs.length === 0 && (
        <EmptyStateCard
          icon="📋"
          title="No audit logs found"
          message="No audit logs match your current filters."
          action={
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          }
        />
      )}
    </div>
  );
};

export default AuditLogsPage;
